import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight } from "lucide-react";

export const PPCSearchGuideModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="fixed top-0 right-0 bottom-0 z-[101] w-[50%] max-w-[960px] bg-slate-900 shadow-2xl flex flex-col border-l border-slate-700"
      >
        {/* Header - gradient bar */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0 bg-gradient-to-r from-violet-600 via-indigo-600 to-violet-600">
          <div className="flex items-center gap-3 min-w-0 mr-4">
            <button onClick={onClose} className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0 font-medium" data-testid="close-ppc-modal">
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span>Back to Report</span>
            </button>
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
              <span className="text-lg">🔍</span>
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm truncate">Understanding PPC, SEM & Paid Search</p>
              <p className="text-white/60 text-xs">A guide to pay-per-click advertising</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/15 transition-colors">
            <X className="w-4 h-4 text-white/80" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Introduction */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6 border border-slate-700">
            <p className="text-slate-300 text-sm leading-relaxed">
              Pay Per Click (PPC) advertising, like Google Ads, can be powerful, but it's not always necessary. 
              If you already have strong brand awareness, good SEO, and other advertising running, 
              you might not need to pay for every click. Let's break it down.
            </p>
          </div>
          
          {/* Also Known As section */}
          <div className="bg-violet-500/10 rounded-xl p-4 mb-6 border border-violet-500/30">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center">
                <span className="text-violet-400 text-xs">=</span>
              </div>
              <h3 className="text-sm font-semibold text-violet-400">Also Known As...</h3>
            </div>
            <p className="text-slate-300 text-sm mb-3">
              You might hear these terms used interchangeably. They&apos;re all essentially the same thing:
            </p>
            <div className="flex flex-wrap gap-2">
              {['PPC', 'Pay Per Click', 'Google Ads', 'AdWords', 'Performance Max', 'Paid Search', 'SEM', 'Search Engine Marketing', 'Sponsored Ads', 'Search Ads'].map((term) => (
                <span key={term} className="text-xs px-2.5 py-1 rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/30">
                  {term}
                </span>
              ))}
            </div>
            <p className="text-slate-400 text-xs mt-3 italic">
              "AdWords" was Google's original name before rebranding to "Google Ads" in 2018: same service, different name.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Pros */}
            <div className="bg-emerald-500/10 rounded-xl p-5 border border-emerald-500/30">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <span className="text-emerald-400 font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-emerald-400">Pros of PPC</h3>
              </div>
              <ul className="space-y-3">
                {[
                  { title: 'Instant Visibility', desc: 'Show up at the top of search results immediately' },
                  { title: 'Highly Targeted', desc: 'Reach people actively searching for your services' },
                  { title: 'Measurable ROI', desc: 'Track exactly which clicks turn into customers' },
                  { title: 'Competitive Defense', desc: 'Prevent competitors from taking your brand searches' },
                  { title: 'New Market Entry', desc: 'Great when launching in new service areas' },
                ].map(item => (
                  <li key={item.title} className="flex items-start gap-2">
                    <span className="text-emerald-400 mt-1">•</span>
                    <div>
                      <p className="text-white font-medium text-sm">{item.title}</p>
                      <p className="text-slate-400 text-xs">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Cons */}
            <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/30">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <span className="text-amber-400 font-bold">!</span>
                </div>
                <h3 className="text-lg font-semibold text-amber-400">Cons of PPC</h3>
              </div>
              <ul className="space-y-3">
                {[
                  { title: 'Costs Add Up', desc: 'You pay for every click, whether they convert or not' },
                  { title: 'Stops When You Stop', desc: 'Unlike SEO, traffic disappears when budget runs out' },
                  { title: 'Competitive Bidding Wars', desc: 'Popular keywords can be expensive in your industry' },
                  { title: 'Click Fraud Risk', desc: 'Competitors or bots can waste your ad budget' },
                  { title: 'May Be Redundant', desc: 'If SEO already ranks you #1, why pay for the same click?' },
                ].map(item => (
                  <li key={item.title} className="flex items-start gap-2">
                    <span className="text-amber-400 mt-1">•</span>
                    <div>
                      <p className="text-white font-medium text-sm">{item.title}</p>
                      <p className="text-slate-400 text-xs">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* When You Might NOT Need PPC */}
          <div className="bg-violet-500/10 rounded-xl p-5 border border-violet-500/30 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">🎯</span>
              <h3 className="text-lg font-semibold text-violet-400">When You Might NOT Need PPC</h3>
            </div>
            <ul className="space-y-2 text-sm text-slate-300">
              {[
                { check: 'Your SEO already ranks you on page one', bold: 'SEO already ranks you on page one' },
                { check: 'You have strong brand awareness from other advertising (TV, radio, social)', bold: 'brand awareness' },
                { check: 'Most of your leads come from referrals and repeat customers', bold: 'referrals and repeat customers' },
                { check: 'Your Google Business Profile appears prominently in local searches', bold: 'Google Business Profile' },
                { check: 'People are already searching for your brand name directly', bold: 'searching for your brand name directly' },
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="text-violet-400">✓</span>
                  <span>{item.check}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Bottom Line */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-5 border border-slate-600">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">💡</span>
              <h3 className="text-lg font-semibold text-white">The Bottom Line</h3>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              PPC is a <strong className="text-white">tool, not a requirement</strong>. If your brand is already visible and 
              people are finding you organically, your advertising dollars might be better spent on 
              <strong className="text-white"> brand awareness</strong> (top of funnel) rather than paying for clicks from 
              people who would have found you anyway. The best strategy depends on your specific situation.
            </p>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
